package com.evoapps.evoshift.domain.usecase.user

import com.evoapps.evoshift.domain.market.payroll.PayrollProfile
import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.Competence
import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.repository.UserRepository

class UpdateEmployeeUseCase(private val userRepository: UserRepository) {

    suspend operator fun invoke(
        userId: String,
        workplaceId: String,
        workplaceName: String,

        // Public Profile Data
        displayName: String,

        // Engagement Data (Public per workplace)
        role: String,
        permission: UserPermission,
        skills: List<String>,
        isPrimary: Boolean,
        competence: Competence = Competence.INDEPENDENT,

        // Financial Data (Sensitive per workplace)
        contractTypeId: String,
        hourlyRate: Double?,
        monthlySalary: Double?,
        employerCost: Double = 0.0, // unused: the market's payroll rules compute it
        fte: Double = 1.0, // Domyślnie pełny etat
        declaredNumberOfHours: Int? = null,
        currency: String = "PLN",
        payrollProfile: PayrollProfile = PayrollProfile(),

        // Personal Data (Sensitive General)
        email: String?,
        phoneNumber: String?,
        address: String?,
        bankAccountNumber: String?,
        /**
         * Pay and personal data are written only by those who may (Capability.MANAGE_PAY); an
         * operational manager's save changes the profile and engagement and leaves them as they are.
         */
        includeSensitive: Boolean = true,
        /**
         * SH-57: the pay terms as they were (their history is kept), and the day ("YYYY-MM-DD") new
         * pay applies from; earlier days keep the old pay. Null: the change applies from the start.
         */
        previous: FinancialEngagement? = null,
        rateEffectiveFrom: String? = null,
        /**
         * SH-149: the day ("YYYY-MM-DD") changed payroll facts (student, under 26, PIT-2, PPK...)
         * apply from; earlier days keep the previous facts. Null: the change applies from the start.
         */
        profileEffectiveFrom: String? = null,
        /** SH-117: the number in the payroll software (RCP card); kept as it was unless given. */
        payrollId: String? = previous?.payrollId
    ): Result<Unit> {
        return try {
            // 1. Logika tworzenia modelu wynagrodzenia (Sealed Class)
            // Usuwamy employerCost - będzie liczony dynamicznie w raportach
            val compensation = if (monthlySalary != null && monthlySalary > 0) {
                CompensationModel.MonthlyFixed(
                    baseSalary = monthlySalary,
                    fte = fte,
                    currency = currency,
                    employerCost = employerCost
                )
            } else {
                CompensationModel.Hourly(
                    rate = hourlyRate ?: 0.0,
                    currency = currency,
                    employerCost = employerCost
                )
            }

            val id = payrollId?.trim()?.takeIf { it.isNotEmpty() }
            val financialEngagement = previous?.copy(
                contractTypeId = contractTypeId,
                declaredNumberOfHours = declaredNumberOfHours,
                payrollId = id
            // SH-250: periods with the same terms one after another are stored as one.
            )?.withPay(compensation, rateEffectiveFrom)?.withProfile(payrollProfile, profileEffectiveFrom)?.withMergedPeriods() ?: FinancialEngagement(
                contractTypeId = contractTypeId,
                declaredNumberOfHours = declaredNumberOfHours,
                compensation = compensation,
                payrollProfile = payrollProfile,
                payrollId = id
            )

            // 2. Przekazanie do repozytorium
            // Repozytorium powinno użyć batcha, żeby zaktualizować:
            // - users/{uid} (displayName, engagement map)
            // - users/{uid}/sensitive/general (phone, address, email)
            // - users/{uid}/sensitive/{workplaceId} (financialEngagement)

            userRepository.updateEmployeeDetailsFull(
                userId = userId,
                workplaceId = workplaceId,
                workplaceName = workplaceName,
                displayName = displayName,

                // Engagement params
                role = role,
                permission = permission,
                skills = skills,
                isPrimary = isPrimary,

                // Sensitive params
                financial = financialEngagement,
                email = email,
                phoneNumber = phoneNumber,
                address = address,
                bankAccountNumber = bankAccountNumber,
                includeSensitive = includeSensitive,
                competence = competence
            )

            // Metoda repozytorium powinna zwracać Result<Unit>, więc po prostu go zwracamy
            // (zakładając, że updateEmployeeDetailsFull zwraca Result)
            // Jeśli repo rzuca wyjątek, złapie go catch poniżej.

        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}